import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { showsAgentWork } from '../../../../lib/agent-work';
import { useTranslation } from '../../../../lib/i18n';
import { messageDir } from '../../../../lib/message-direction';
import { handleModalEscape, pushModalEscape } from '../../../../lib/modal-escape';
import { Markdown } from '../../../Markdown';
import { BackButton, Badge, Button, OverflowMenu, TaskRowBadge, ScrollArea, Typography } from '../../ui';
import { AgentConversationCard } from '../AgentConversationCard';
import { DashboardBuildResultCard } from '../DashboardBuildResultCard';
import { CancelTaskConfirm } from '../CancelTaskConfirm';
import { TaskActivityTimeline } from '../TaskActivityTimeline';
import { TaskFilesSection } from '../TaskFilesSection';
import { TaskSentOutreachSection } from '../TaskSentOutreachSection';
import { TaskHeldSendsSection } from '../../send-hold/TaskHeldSendsSection';
import { TaskChatOriginLine, TaskOriginLine, TaskRoutineOriginLine, TaskScheduledLine } from '../TaskOriginLine';
import { TaskBriefSection, TaskNotesSection } from '../TaskUserEdits';
import { useTaskDetailModal } from '../use-task-detail-modal';
import { TaskDecisionPrompts } from '../TaskDecisionPrompts';
import { postPreviewHoldsDecision } from '../../../../lib/task-post-preview';
import { TASK_DETAILS_PAGE } from '../../../../lib/tasks-bi';
import { SocialPostPreviewCard } from '../../chat-surface/MessageRow/SocialPostPreviewCard';
import { SocialEditFirstChip, TaskContentSection } from '../TaskContentSection';
import taskStyles from '../TaskDetailsPanel.module.css';
import styles from './TaskDetailMobile.module.css';

// The narrow task-details drill-in (Figma "Single Task [2.0]", 20846-92371): a full-screen sheet rather
// than a centered card — a back control over the title, origin, created date, status badge and description
// on the standard grey surface; the briefing and the agent-activity timeline on a white band; the files,
// the notes and the agent's conversation back on the grey; and a pinned full-width primary CTA. Always full-screen (it is only mounted below `useNarrow`, so unlike the
// shared Modal's `fullScreenOnSmall` there is no centered-card range to guard against). Behaviour, copy
// and analytics are the shared useTaskDetailModal — only the layout differs from the desktop modal.
export function TaskDetailMobile({ taskId }: { taskId: string }) {
  const vm = useTaskDetailModal(taskId);
  const { t } = useTranslation();
  const postPreview = vm.kind === 'ready' ? postPreviewHoldsDecision(vm.task.socialPostPreviewCard, vm.task.decisionPrompts, vm.answeredDecisionIds) : null;
  const scheduledWhen = vm.kind === 'ready' && !vm.content ? vm.scheduledWhen : '';

  const sheetRef = useRef<HTMLDivElement>(null);
  const entranceRef = useRef<Animation | null>(null);
  const closingRef = useRef(false);
  // Read the dismissal through a ref so a fresh handler each render doesn't re-register the escape stack.
  //
  // On a full-screen sheet the top-bar control IS a back control, not a close control — so when this
  // task was opened from a goal it goes BACK to that goal, exactly as a pushed screen does on both
  // phones, rather than dropping the reader out to the grid. That is not the "close became back" trap
  // the desktop card guards against: there is no ✕ here to overload, and the goal underneath keeps its
  // own way out. With nothing underneath it closes, as it always did.
  const closeRef = useRef(vm.close);
  closeRef.current = vm.back ? vm.back.onBack : vm.close;

  // Dismiss with a slide-down, then unmount. The openModal/closeModal slot owns the sheet's lifetime,
  // so it would otherwise vanish instantly — we play the exit first and call close() on finish. Held
  // in a ref (kept fresh each render) so the mount-once Escape listener always calls the latest one.
  // Reentrancy-guarded so Escape + a tap can't both fire. Reduced motion (or no element) closes at once.
  const requestCloseRef = useRef<() => void>(() => {});
  requestCloseRef.current = () => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      closeRef.current();
      return;
    }
    // Start from wherever the sheet visually is (mid-open close doesn't snap to the top first); WAAPI
    // commits the start keyframe atomically, and `fill: 'forwards'` holds it off-screen until unmount.
    const from = getComputedStyle(el).transform;
    entranceRef.current?.cancel();
    const exit = el.animate([{ transform: from === 'none' ? 'translateY(0)' : from }, { transform: 'translateY(100%)' }], {
      duration: 260,
      easing: 'cubic-bezier(0.4, 0, 1, 1)',
      fill: 'forwards',
    });
    exit.onfinish = () => closeRef.current();
    exit.oncancel = () => closeRef.current();
  };

  // Slide up from the bottom on open, like a pushed screen. Driven by the Web Animations API rather than
  // a mount-time CSS transition: it commits the off-screen start keyframe atomically on the first frame
  // (no flash, no rAF/state race), runs on the compositor, and skips entirely under reduced-motion.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const anim = el.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
      duration: 300,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
    entranceRef.current = anim;
    return () => anim.cancel();
  }, []);

  // Claim Escape for this sheet the same way the DS Modal does (capture-phase, innermost-first) so it
  // closes — with the slide-down — without unwinding any panel underneath.
  useEffect(() => {
    const pop = pushModalEscape(() => requestCloseRef.current());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div ref={sheetRef} className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="task-detail-title">
      <div className={styles.topBar}>
        <BackButton onClick={() => requestCloseRef.current()} />
        {/* Name the destination when there is one. A bare chevron on a stacked sheet cannot say whether
            it returns to the goal or leaves entirely, and that ambiguity is the reported bug one level
            down. */}
        {vm.back && <span className={styles.backToLabel}>{vm.back.label}</span>}
      </div>

      {vm.kind === 'error' ? (
        <div className={styles.stateRegion}>{t('tasks.detail.notFound')}</div>
      ) : vm.kind === 'loading' ? (
        <SheetShell
          title={
            <div className={styles.skelStack}>
              <Shimmer w="85%" h={22} />
              <Shimmer w="55%" h={22} />
            </div>
          }
          facts={
            <>
              <Shimmer w={96} h={20} r={8} />
              <div className={styles.skelStack}>
                <Shimmer w="100%" h={14} />
                <Shimmer w="80%" h={14} />
              </div>
            </>
          }
          band={<TimelineSkeleton />}
          footer={<Shimmer w="100%" h={42} r={8} />}
        />
      ) : (
        <>
        <SheetShell
          titleId="task-detail-title"
          title={<Typography variant="h2" className={styles.titleText}>{vm.title}</Typography>}
          action={
            // The 3-dot actions menu beside the hero title, top-aligned (Figma 21055-244282 / 21055-244346):
            // Cancel Task while the task is still open, plus a displaced Maestro handoff. Mirrors the native
            // Task screen's title-row menu.
            vm.menu.length > 0 ? (
              <OverflowMenu
                ariaLabel={t('tasks.rowMenu.label', { title: vm.title })}
                actions={vm.menu.map((item) => ({
                  key: item.key,
                  label: item.label,
                  destructive: item.destructive,
                  onSelect: item.onClick,
                }))}
              />
            ) : undefined
          }
          facts={
            <>
              {/* Provenance first — the goal that laid it out, otherwise the chat it was born in, never
                  both — then when it starts, when it was created, and where it stands. */}
              {(vm.routineOrigin || vm.goalOrigin || vm.chatOrigin || scheduledWhen || vm.created) && (
                <div className={styles.metaStack}>
                  {vm.routineOrigin && <TaskRoutineOriginLine origin={vm.routineOrigin} onOpen={vm.onOpenRoutineOrigin} />}
                  {vm.goalOrigin && <TaskOriginLine origin={vm.goalOrigin} detail />}
                  {vm.chatOrigin && <TaskChatOriginLine onOpen={vm.onOpenChatOrigin} />}
                  {scheduledWhen && <TaskScheduledLine when={scheduledWhen} />}
                  {vm.created && <div className={styles.created}>{t('tasks.detail.created', { date: vm.created })}</div>}
                </div>
              )}
              {vm.scheduledLabel ? (
                vm.content ? null : <Badge variant="waiting-on-a-reply" label={vm.scheduledLabel} />
              ) : vm.gating && !vm.awaitingDecision ? (
                <Badge variant="waiting-on-a-reply" label={t('tasks.status.after', { title: vm.gating.title })} />
              ) : (
                <TaskRowBadge task={vm.task} detail />
              )}
              {vm.content ? (
                <div className={styles.content}>
                  <TaskContentSection {...vm.content} />
                </div>
              ) : postPreview ? (
                <SocialPostPreviewCard card={postPreview} />
              ) : (
                vm.description && (
                  <div className={styles.desc}>
                    <Markdown variant="detail" dir={messageDir(vm.description)}>
                      {vm.description}
                    </Markdown>
                  </div>
                )
              )}
              {/* The question this task is held on, as the desktop drill-in shows it. It was missing here, so a
                  step waiting on its plan's approval read "Waiting on you" with nothing to answer. A plan question
                  shows "See the full plan", which opens the goal (`planReviewGoalId`). */}
              <TaskDecisionPrompts task={vm.task} origin={vm.goalOrigin} onAnsweredChange={vm.onDecisionAnswered} page={TASK_DETAILS_PAGE} postAt={vm.task.socialPostPreviewCard?.scheduledAt}
          afterFirstChip={vm.socialEditFirst ? { decisionId: vm.socialEditFirst.decisionId, node: <SocialEditFirstChip onPress={vm.socialEditFirst.onPress} /> } : undefined}
          scheduleLabelsFor={vm.scheduleLabelsFor}
        />
            </>
          }
          band={
            // Every child of the band can render nothing; with none of them the band is omitted rather
            // than drawn as an empty white strip.
            (vm.task.brief?.trim() || vm.editable || vm.task.steps.length > 0 || vm.sentOutreaches?.length) ? (
            <>
              <TaskBriefSection task={vm.task} editable={vm.editable} edits={vm.edits} />
              <TaskActivityTimeline
                agentId={vm.task.agentId}
                agentName={vm.agentName}
                steps={vm.task.steps}
                currentStep={vm.currentStep}
                statusVariant={vm.statusVariant}
              />
              {/* What this task wrote that is still WAITING to go (the send rule), above what already went. */}
              <TaskHeldSendsSection taskId={taskId} />
              <TaskSentOutreachSection outreaches={vm.sentOutreaches} />
            </>
            ) : null
          }
          lower={
            <>
              <TaskFilesSection files={vm.files} onOpen={vm.onOpenFile} />
              {/* The briefing above and the notes here are the drill-in's two writes — one shared module
                  with the desktop modal, so the two layouts cannot grow two different forms. */}
              <TaskNotesSection notes={vm.notes ?? []} editable={vm.editable} edits={vm.edits} agentName={vm.agentName} layout="stacked" className={styles.notes} />
              {/* A dashboard build has no agent conversation: its section shows what the BUILD produced. A task
                  that finished with no agent ever on it shows no section at all (`showsAgentWork`). */}
              {vm.task.isDashboardBuild ? (
                <DashboardBuildResultCard status={vm.task.status} marker={vm.task.dashboardBuild} changeLog={vm.task.changeLog} className={styles.conversation} />
              ) : showsAgentWork(vm.task) ? (
                <AgentConversationCard taskId={taskId} className={vm.content ? `${styles.conversation} ${styles.flushConversation}` : styles.conversation} />
              ) : null}
            </>
          }
          footer={
            <div className={styles.footerActions}>
              {/* The sheet's back chevron is its dismissal, so unlike the desktop modal there is no
                  "Close" to disambiguate here — the pinned CTA stands alone. */}
              <Button fullWidth size="large" onClick={vm.cta.onClick}>
                {vm.cta.label}
              </Button>
            </div>
          }
        />
        {/* Confirm-then-cancel, shared with the desktop modal and the list row's menu. */}
        <CancelTaskConfirm flow={vm.cancel} />
        </>
      )}
    </div>,
    document.body,
  );
}

// The sheet's body layout — the same regions whether loading or ready, so the skeleton and the real
// content occupy identical structure and the swap between them never reflows.
function SheetShell({
  titleId,
  title,
  action,
  facts,
  band,
  lower,
  footer,
}: {
  titleId?: string;
  title: ReactNode;
  /** An optional control at the title's trailing edge, top-aligned with the first line (the ⋮ menu). */
  action?: ReactNode;
  facts: ReactNode;
  band?: ReactNode;
  lower?: ReactNode;
  footer: ReactNode;
}) {
  return (
    <>
      <ScrollArea className={styles.scroll}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <div className={styles.title} id={titleId}>
              {title}
            </div>
            {action}
          </div>
          <div className={styles.statusStack}>{facts}</div>
        </div>
        {band && <div className={styles.band}>{band}</div>}
        {lower && <div className={styles.lower}>{lower}</div>}
      </ScrollArea>
      <div className={styles.footer}>{footer}</div>
    </>
  );
}

function Shimmer({ w, h, r = 6 }: { w: number | string; h: number; r?: number }) {
  return <div className="orion-shimmer" style={{ width: w, height: h, borderRadius: r }} />;
}

// The activity placeholder — the same agent row + left-ruled step list the ready timeline draws.
function TimelineSkeleton() {
  return (
    <div className={taskStyles.activity}>
      <div className={taskStyles.agent}>
        <Shimmer w={15} h={15} r={8} />
        <Shimmer w={80} h={14} />
      </div>
      <div className={taskStyles.timeline}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={taskStyles.step}>
            <Shimmer w={16} h={16} r={8} />
            <Shimmer w={i === 1 ? '70%' : '88%'} h={14} />
          </div>
        ))}
      </div>
    </div>
  );
}
